import { create } from '@bufbuild/protobuf'
import { createConnectQueryKey, useMutation, useQuery } from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'
import { Link, createFileRoute } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import {
  Archive,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Loader2,
  Users,
} from 'lucide-react'
import { toast } from 'sonner'
import {
  archiveAnnouncement,
  listAnnouncementReads,
  listAnnouncements,
} from '../../../gen/cortado/v1/admin-AdminService_connectquery'
import {
  ArchiveAnnouncementRequestSchema,
  ListAnnouncementReadsRequestSchema,
  ListAnnouncementsRequestSchema,
  type AnnouncementRead,
} from '../../../gen/cortado/v1/announcements_pb'
import { AnnouncementDisplayTypeEnum } from '../../../gen/cortado/v1/settings_pb'
import { Badge } from '../../../components/ui/badge'
import { Button } from '../../../components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table'
import { formatTimestamp, displayTypeLabel } from './-utils'

export const Route = createFileRoute('/_admin/announcements/$announcementId')({
  component: AnnouncementDetailPage,
})

const ANNOUNCEMENTS_PAGE_SIZE = 100
const READS_PAGE_SIZE = 100

const announcementListKey = createConnectQueryKey({
  schema: listAnnouncements,
  cardinality: undefined,
})

function AnnouncementDetailPage() {
  const { announcementId } = Route.useParams()
  const queryClient = useQueryClient()
  const [announcementSearch, setAnnouncementSearch] = useState({
    announcementId,
    offset: 0,
  })
  const [readOffset, setReadOffset] = useState(0)
  const [confirmOpen, setConfirmOpen] = useState(false)

  useEffect(() => {
    setAnnouncementSearch({ announcementId, offset: 0 })
    setReadOffset(0)
  }, [announcementId])

  const announcementOffset = announcementSearch.announcementId === announcementId
    ? announcementSearch.offset
    : 0

  const announcementRequest = useMemo(
    () =>
      create(ListAnnouncementsRequestSchema, {
        limit: ANNOUNCEMENTS_PAGE_SIZE,
        offset: announcementOffset,
      }),
    [announcementOffset],
  )

  const announcementQuery = useQuery(
    listAnnouncements,
    announcementRequest,
  )
  const announcementsPage = announcementQuery.data?.announcements ?? []
  const announcement = announcementsPage.find((a) => a.id === announcementId)
  const hasMoreAnnouncementPages = announcementsPage.length === ANNOUNCEMENTS_PAGE_SIZE
  const announcementLoading = !announcement && (
    announcementQuery.isLoading
    || announcementQuery.isFetching
    || hasMoreAnnouncementPages
  )
  const isArchived = Boolean(announcement?.archivedAt)

  useEffect(() => {
    if (
      announcementSearch.announcementId !== announcementId
      || announcement
      || announcementQuery.isLoading
      || announcementQuery.isFetching
      || !hasMoreAnnouncementPages
    ) {
      return
    }

    setAnnouncementSearch({
      announcementId,
      offset: announcementOffset + ANNOUNCEMENTS_PAGE_SIZE,
    })
  }, [
    announcement,
    announcementId,
    announcementOffset,
    announcementQuery.isFetching,
    announcementQuery.isLoading,
    announcementSearch.announcementId,
    hasMoreAnnouncementPages,
  ])

  const readRequest = useMemo(
    () =>
      create(ListAnnouncementReadsRequestSchema, {
        announcementId,
        limit: READS_PAGE_SIZE,
        offset: readOffset,
      }),
    [announcementId, readOffset],
  )
  const readsQuery = useQuery(listAnnouncementReads, readRequest)
  const reads = readsQuery.data?.reads ?? []
  const totalReads = readsQuery.data?.totalCount ?? 0
  const canReadPrevious = readOffset > 0
  const canReadNext = readOffset + READS_PAGE_SIZE < totalReads
  const readRangeStart = totalReads === 0 ? 0 : readOffset + 1
  const readRangeEnd = Math.min(readOffset + READS_PAGE_SIZE, totalReads)

  const archiveMutation = useMutation(archiveAnnouncement, {
    onSuccess: async () => {
      setConfirmOpen(false)
      toast.success('Announcement archived')
      await queryClient.invalidateQueries({ queryKey: announcementListKey })
    },
    onError: (error) => {
      toast.error(error.message || 'Unable to archive announcement')
    },
  })

  const primaryLabel = announcement?.primaryButtonLabel?.trim()
    || (announcement?.actionUrl ? 'Open' : '')
  const secondaryLabel = announcement?.secondaryButtonLabel?.trim() || ''

  return (
    <div className="space-y-6">
      <div>
        <Button asChild variant="ghost" size="sm" className="-ml-2 mb-2">
          <Link to="/announcements">
            <ArrowLeft className="h-4 w-4" />
            Back to announcements
          </Link>
        </Button>

        {announcementLoading ? (
          <p className="text-sm text-muted-foreground">Loading announcement...</p>
        ) : !announcement && announcementQuery.isError ? (
          <p className="text-sm text-destructive">
            {announcementQuery.error.message || 'Unable to load announcement.'}
          </p>
        ) : !announcement ? (
          <p className="text-sm text-muted-foreground">Announcement not found.</p>
        ) : (
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-2xl font-bold">{announcement.title}</h1>
                {isArchived && <Badge variant="outline">Archived</Badge>}
              </div>
              <p className="text-sm text-muted-foreground">
                Published {formatTimestamp(announcement.publishedAt)}
                {isArchived && (
                  <>
                    {' · '}
                    Archived {formatTimestamp(announcement.archivedAt)}
                  </>
                )}
              </p>
            </div>
            {!isArchived && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setConfirmOpen(true)}
                disabled={archiveMutation.isPending}
              >
                <Archive className="h-4 w-4" />
                Archive
              </Button>
            )}
          </div>
        )}
      </div>

      {announcement && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Announcement</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={announcement.displayType === AnnouncementDisplayTypeEnum.MODAL ? 'info' : 'secondary'}>
                {displayTypeLabel(announcement.displayType)}
              </Badge>
              {announcement.tag && (
                <Badge variant="outline">{announcement.tag}</Badge>
              )}
              {primaryLabel && (
                <Badge variant="outline">
                  <ExternalLink className="h-3 w-3" />
                  Primary: {primaryLabel}
                </Badge>
              )}
              {secondaryLabel && (
                <Badge variant="outline">Secondary: {secondaryLabel}</Badge>
              )}
            </div>
            {announcement.headerImageUrl && (
              <div className="aspect-[2/1] w-full overflow-hidden rounded-md border bg-muted">
                <img
                  src={announcement.headerImageUrl}
                  alt="Announcement header"
                  className="h-full w-full object-cover"
                />
              </div>
            )}
            <p className="whitespace-pre-wrap text-sm text-muted-foreground">{announcement.body}</p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-4 w-4 text-muted-foreground" />
            Read analytics
          </CardTitle>
          <CardDescription>
            {isArchived
              ? 'New reads are not tracked while the announcement is archived.'
              : 'Unique readers for this announcement.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="text-sm text-muted-foreground">Total reads</p>
              <p className="text-3xl font-semibold tabular-nums">{totalReads}</p>
            </div>
            {totalReads > READS_PAGE_SIZE && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  {readRangeStart}-{readRangeEnd} of {totalReads}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!canReadPrevious || readsQuery.isFetching}
                  onClick={() => setReadOffset((offset) => Math.max(0, offset - READS_PAGE_SIZE))}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!canReadNext || readsQuery.isFetching}
                  onClick={() => setReadOffset((offset) => offset + READS_PAGE_SIZE)}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>

          {readsQuery.isLoading ? (
            <div className="flex items-center gap-2 rounded-md border p-4 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading reads...
            </div>
          ) : readsQuery.isError ? (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
              {readsQuery.error.message || 'Unable to load announcement reads'}
            </div>
          ) : reads.length === 0 ? (
            <div className="rounded-md border p-4 text-sm text-muted-foreground">
              No reads yet
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Reader</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Read time</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reads.map((read, index) => (
                  <TableRow key={`${read.user?.id ?? 'unknown'}-${String(read.readAt?.seconds ?? index)}`}>
                    <TableCell className="font-medium">{readerName(read)}</TableCell>
                    <TableCell className="text-muted-foreground">{read.user?.email || '-'}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatTimestamp(read.readAt)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archive announcement?</DialogTitle>
            <DialogDescription>
              Archiving hides this announcement from all users and stops tracking new reads. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setConfirmOpen(false)}
              disabled={archiveMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() =>
                archiveMutation.mutate(
                  create(ArchiveAnnouncementRequestSchema, { announcementId }),
                )
              }
              disabled={archiveMutation.isPending}
            >
              {archiveMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Archive className="h-4 w-4" />
              )}
              Archive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function readerName(read: AnnouncementRead) {
  const name = [read.user?.firstName, read.user?.lastName]
    .filter(Boolean)
    .join(' ')
    .trim()

  return name || '-'
}
